// config.js
export default {
  environment: {
    scene: {
      backgroundColor: "#f6c9a2",
      fogNear: 0.5,
      fogFar: 5.5,
      // Perspective atmosphérique : brume pêche lumineuse le matin,
      // abricot en fin de parcours (suit sky.journey). Elle sert aussi
      // de couleur d'horizon au ciel, d'où ces teintes chaudes.
      fogColorStart: "#f7d2b0",
      fogColorEnd: "#f6b386",
      environmentIntensity: 0.65
    },
    ambientLight: {
      color: "rgb(255,255,255)",
      intensity: 0.35
    },
    // Lumière d'ambiance directionnelle : ciel froid au-dessus, rebond
    // chaud de l'herbe et de la terre en dessous. Donne du volume aux
    // pentes à l'ombre là où une ambiante uniforme les aplatissait.
    hemisphereLight: {
      skyColor: "#bcd9ff",
      groundColor: "#7c8a46",
      intensity: 1.45
    },
    directionalLight: {
      color: "rgb(255,255,255)",
      intensity: 2.6,
      position: { x: 10.5, y: 6.7, z: 3 },
      // Le trajet s'assombrit et se réchauffe légèrement vers la fin du
      // parcours (fin d'après-midi). Les valeurs par défaut ci-dessus
      // restent le point médian du trajet (voir sky.journey).
      journey: {
        enabled: true,
        colorStart: "rgb(255,244,226)",
        colorEnd: "rgb(255,206,160)",
        intensityStart: 2.9,
        intensityEnd: 2.5
      }
    },
    sky: {
      enabled: true,
      turbidity: 3.5,
      rayleigh: 2.25,
      mieCoefficient: 0.0125,
      mieDirectionalG: 0.63,
      inclination: 0.05,
      azimuth: 0.25,
      // Fondu de l'horizon dans la brume (voir Environment.setSkyHaze)
      haze: { amount: 0.75, height: 0.12, gain: 0.82 },
      // Dégradé stylisé posé sur le ciel physique : bleu franc au zénith,
      // bleu clair puis orangé vers l'horizon, halo chaud autour du soleil.
      gradient: {
        zenith: "#2463cc",
        middle: "#7fb8f0",
        horizon: "#ffab6e",
        amount: 0.85,
        intensity: 1.1,
        sunGlow: "#ffb066",
        sunGlowStrength: 0.55
      },
      // Arc lumineux sur toute la durée du scroll : les valeurs ci-dessus
      // servent de point médian (~milieu du parcours), start/end s'en
      // écartent modérément pour une matinée plus claire et une fin
      // plus chaude, sans jamais s'éloigner du rendu déjà validé.
      journey: {
        enabled: true,
        start: { turbidity: 2.4, rayleigh: 2.1, mieCoefficient: 0.0045, mieDirectionalG: 0.78, inclination: 0.075, azimuth: 0.22, exposure: 1.08 },
        end: { turbidity: 4.2, rayleigh: 1.7, mieCoefficient: 0.007, mieDirectionalG: 0.8, inclination: 0.025, azimuth: 0.28, exposure: 0.9 }
      }
    }
  },
  terrain: {
    displacementScale: 1,
    emissiveIntensity: 0.5,
    emissiveColor: "#ffffff",
    detailRepeat: 30,
    detailIntensity: 0.075,
    microNormalStrength: 0.08,
    // Modelé des collines (normale reconstruite depuis la heightmap)
    reliefStrength: 2.2,
    macroVariation: 0.07,
    canopyOcclusion: 0.55,
    waterColor: "#145a7e",
    stripeSoftening: 0.38,
    greenToning: 0.45,
    buildingColor: "#ffffff",
    roadColor: "#ffffff",
    pathColor: "#ffffff",
    greenColor: "#4fe54d",
    bunkerColor: "#e5c24c",
    particlesColor: "#aeb582"
  },
  trees: {
    colorA: '#a8d97a',
    colorB: '#e2b75e',
    hue: 0.12,
    saturation: 0.28,
    lightness: 0.2,
    opacity: 1,
    backlight: 0.32,
    baseOcclusion: 0.38,
    wind: 0.018,
    count: 100000,
    scale: 0.002,
    dynamicBillboardRadius: 3.5,
    spatialCellSize: 0.5,
    spawnRadiusNormalized: 1.5,
    minDensityThreshold: 0.06,
    minCameraMove: 0.08,
    minCameraYawDelta: 0.025,
    updateIntervalMs: 66
  },
  quality: {
    preset: 'auto',
    auto: {
      downshiftFps: 52,
      upshiftFps: 58,
      sampleWindowMs: 1800
    },
    presets: {
      medium: {
        rendererPixelRatioMax: 1,
        composerPixelRatio: 0.75,
        composerScaleFactor: 1.5,
        treesCountMultiplier: 0.45,
        shadowMapSize: 1024,
        msaaSamples: 0,
        dofEnabled: false,
        bloomEnabled: false
      },
      high: {
        rendererPixelRatioMax: 1.25,
        composerPixelRatio: 0.9,
        composerScaleFactor: 1.2,
        treesCountMultiplier: 0.7,
        shadowMapSize: 1536,
        msaaSamples: 4,
        dofEnabled: false,
        bloomEnabled: false
      },
      ultra: {
        rendererPixelRatioMax: 1.5,
        composerPixelRatio: 1,
        composerScaleFactor: 1,
        treesCountMultiplier: 1,
        shadowMapSize: 2048,
        msaaSamples: 4,
        dofEnabled: true,
        bloomEnabled: false
      }
    }
  },
  data: {},
  camera: {
    controlMode: 'scroll',
    position: { x: -2, y: 1, z: 0 },
    fov: 75,
    fovMobileOffset: 15,
    near: 0.01,
    far: 500,

    timeline: {
      target: { x: 0, y: 0, z: 0 },
      scroll: {
        lerp: 0.09,
        wheelMultiplier: 0.82,
        touchMultiplier: 1.3,
        syncTouchLerp: 0.07,
        lerpMobile: 0.12,
        syncTouchLerpMobile: 0.09,
        touchMultiplierMobile: 1.6
      },
      intro: {
        position: { x: -1, y: 2.5, z: 0 },
        target: { x: 0, y: 0, z: 0 }

      },
      outro: {
        position: { x: -1, y: 2.5, z: -1 },
        target: { x: 0, y: 0, z: 0 }
      }
    },
    // Exploration libre (OrbitControls) : forte inertie et vitesses
    // modérées pour un déplacement de drone, jamais nerveux.
    freeControls: {
      dampingFactor: 0.05,
      panSpeed: 0.55,
      rotateSpeed: 0.4,
      zoomSpeed: 0.5,
      // Vitesse (1/s) à laquelle le pivot et la caméra épousent le relief
      groundFollow: 4,
      groundClearance: 0.06
    },
    // Plan du parcours (vue orthographique d'ensemble)
    map: {
      elevation: 58,
      padding: 1.22,
      markerScreenSize: 0.12,
      // Inclinaison maximale depuis la verticale (clic droit)
      maxTilt: Math.PI / 3
    },
    cinematic: {
      drift: {
        enabled: true,
        positionAmplitude: 0.006,
        lookAmplitude: 0.012,
        frequency: 0.16
      },
      fovBreathing: {
        enabled: true,
        amplitude: 0.3,
        frequency: 0.12
      },
      banking: {
        enabled: true,
        gain: 0.055,
        maxDegrees: 1.1,
        damping: 2.2
      },
      speedFov: {
        enabled: true,
        gain: 45,
        maxDegrees: 2.6,
        damping: 2.4
      }
    }
  },
  // Visite guidée générée depuis les départs et les greens (voir
  // Core/CourseRoute.js) : un plan-séquence par trou, dont la fenêtre de
  // progression est proportionnelle à sa longueur, et un fondu au noir
  // entre deux plans.
  course: {
    holesStart: 0.035,
    holesEnd: 0.96,
    baseWeight: 0.9,
    // Largeur (en progression) du fondu de part et d'autre d'une coupe
    fadeWidth: 0.006,
    // Temps mort (en progression) à l'entrée d'un trou, après le fondu,
    // avant que la caméra et le tracé ne démarrent
    holdWidth: 0.004,
    // Panneau de fin (appel à l'action) pendant l'outro
    finale: { start: 0.965, end: 0.985 },
    // 0 = vitesse constante, 1 = départ et arrivée très amortis
    shotEasing: 0.6,
    samplesPerShot: 240,
    // Descente d'ouverture en spirale (bouton « Démarrer le parcours »)
    flyover: {
      duration: 12,
      startHeight: 6.5,
      // Rayon de départ autour du point visé par le premier plan du trou 1
      startRadius: 3.4,
      // Nombre de tours (0.5 = demi-tour) et sens (1 ou -1)
      turns: 0.5,
      direction: 1,
      // Inclinaison du regard sous l'horizon au départ (degrés)
      startPitch: 16,
      // Plongée supplémentaire du regard au milieu de la spirale
      midDip: 0.6,
      // Couche de nuages traversée pendant la descente
      cloudHeight: 3.7,
      cloudCount: 90,
      cloudCountMobile: 45
    },
    camera: {
      teeBack: 0.3,
      teeHeight: 0.24,
      teeLookAhead: 1.1,
      flyHeight: 0.42,
      // Écart latéral de l'arc de survol (alterné d'un trou à l'autre)
      arc: 0.14,
      greenBack: 0.38,
      greenHeight: 0.2,
      clearance: 0.1
    }
  },
  postprocessing: {
    filmPass: { enabled: false, intensity: { value: 0.5 } },
    vignettePass: { enabled: true, offset: 0.3, darkness: 1.4 },
    bloomPass: { enabled: false, strength: 0.2, radius: 0.05, threshold: 0.9 },
    bokehPass: { enabled: false, focus: 1, aperture: 0.0025, maxblur: 0.004 },
    gammaCorrectionPass: { enabled: true },
    // Étalonnage final (tone mapping ACES + grade). Quand il est actif,
    // il remplace vignettePass (dont il reprend les réglages) et gamma.
    gradePass: {
      enabled: true,
      contrast: 1.05,
      saturation: 1.14,
      shadowTint: '#6f84a6',
      highlightTint: '#a68a6c',
      splitToneStrength: 0.24,
      grain: 0.018,
      grainMobile: 0.012
    }
  }
};
